import { useState, useEffect, useMemo } from 'react';
import { getTeacherSchedule, searchInstructors, Instructor, updateActivitySlotStatus, bulkUpdateActivitySlotStatus } from '../../_services/schedules.service';
import type {
  TeacherScheduleData,
  TeacherScheduleFilters,
  ActivitySlot,
  GroupedByDate,
  GroupedByTimetable,
} from '../../_models/schedule.model';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import StudentListDialog from './components/StudentListDialog';
import './teacher-schedule.css';

type ViewMode = 'date' | 'timetable' | 'grid';

interface TeacherScheduleProps {
  teacherId?: number;
}

const TeacherSchedule: React.FC<TeacherScheduleProps> = ({ teacherId }) => {
  // Helper function to get default date range (current week)
  const getDefaultDateRange = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Saturday)

    return {
      startDate: startOfWeek.toISOString().split('T')[0],
      endDate: endOfWeek.toISOString().split('T')[0],
    };
  };

  const [scheduleData, setScheduleData] = useState<TeacherScheduleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('date');
  const [filters, setFilters] = useState<TeacherScheduleFilters>(getDefaultDateRange());
  const [showFilters, setShowFilters] = useState(false);

  // Instructor search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Instructor[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Export dropdown state
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Bulk update states
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<'Scheduled' | 'Conducted' | 'Cancelled'>('Conducted');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Quick action states
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [updatingSlot, setUpdatingSlot] = useState<number | null>(null);

  // Confirmation dialog states
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    slotId?: number;
    slotIds?: number[];
    status: 'Scheduled' | 'Conducted' | 'Cancelled';
    isBulk: boolean;
  } | null>(null);

  // Student list dialog states
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [selectedSlotForStudents, setSelectedSlotForStudents] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Get current user's teacher ID from localStorage
  const getCurrentTeacherId = (): number | null => {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser && currentUser !== 'undefined') {
      try {
        const user = JSON.parse(currentUser);
        return user.employeeId || user.id || null;
      } catch (error) {
        console.error('Failed to parse currentUser:', error);
        return null;
      }
    }
    return null;
  };

  const effectiveTeacherId = selectedInstructor?.id || teacherId || getCurrentTeacherId();

  // Handle instructor search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const response = await searchInstructors(searchQuery);
        if (response.success && response.data) {
          setSearchResults(response.data);
          setShowSearchResults(true);
        }
      } catch (err) {
        console.error('Error searching instructors:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300); // Debounce search

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  useEffect(() => {
    if (!effectiveTeacherId) {
      // No teacher ID yet - user needs to search for an instructor
      setLoading(false);
      setScheduleData(null);
      setError(null);
      return;
    }

    loadSchedule();
  }, [effectiveTeacherId, filters]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.slot-actions')) {
        setOpenActionMenu(null);
      }
    };

    if (openActionMenu !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openActionMenu]);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTeacherSchedule(effectiveTeacherId!, filters);
      if (response.success && response.data) {
        setScheduleData(response.data);
      } else {
        setError(response.message || 'Failed to load schedule');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof TeacherScheduleFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  // Bulk mode handlers
  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedSlots(new Set());
  };

  const toggleSlotSelection = (slotId: number) => {
    const newSelected = new Set(selectedSlots);
    if (newSelected.has(slotId)) {
      newSelected.delete(slotId);
    } else {
      newSelected.add(slotId);
    }
    setSelectedSlots(newSelected);
  };

  const selectAllSlots = () => {
    if (!scheduleData) return;
    const allSlotIds = new Set(scheduleData.slots.map(slot => slot.id));
    setSelectedSlots(allSlotIds);
  };

  const deselectAllSlots = () => {
    setSelectedSlots(new Set());
  };

  // Status update handlers
  const handleQuickStatusUpdate = (slotId: number, status: 'Scheduled' | 'Conducted' | 'Cancelled') => {
    setConfirmationData({
      slotId,
      status,
      isBulk: false,
    });
    setShowConfirmation(true);
    setOpenActionMenu(null);
  };

  const handleBulkStatusUpdate = () => {
    if (selectedSlots.size === 0) {
      alert('Please select at least one slot to update');
      return;
    }

    setConfirmationData({
      slotIds: Array.from(selectedSlots),
      status: bulkStatus,
      isBulk: true,
    });
    setShowConfirmation(true);
  };

  const confirmStatusUpdate = async () => {
    if (!confirmationData) return;

    try {
      if (confirmationData.isBulk && confirmationData.slotIds) {
        setBulkUpdating(true);
        const response = await bulkUpdateActivitySlotStatus(
          confirmationData.slotIds,
          confirmationData.status
        );

        if (response.success) {
          alert(`✓ Successfully updated ${response.data?.successCount || 0} slots`);
          setSelectedSlots(new Set());
          setBulkMode(false);
          await loadSchedule(); // Reload data to reflect changes
        } else {
          alert('✗ Failed to update slots. Please try again.');
        }
      } else if (confirmationData.slotId) {
        setUpdatingSlot(confirmationData.slotId);
        const response = await updateActivitySlotStatus(
          confirmationData.slotId,
          confirmationData.status
        );

        if (response.success) {
          await loadSchedule(); // Reload data to reflect changes
        } else {
          alert('✗ Failed to update slot status. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('✗ An error occurred while updating the status');
    } finally {
      setShowConfirmation(false);
      setConfirmationData(null);
      setUpdatingSlot(null);
      setBulkUpdating(false);
    }
  };

  const cancelStatusUpdate = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  const handleShowStudents = (slot: ActivitySlot) => {
    setSelectedSlotForStudents({
      id: slot.id,
      name: slot.name,
    });
    setStudentDialogOpen(true);
  };

  const handleCloseStudentDialog = () => {
    setStudentDialogOpen(false);
    setSelectedSlotForStudents(null);
  };

  const formatTime = (time?: string): string => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'No Date';
    const date = new Date(dateStr);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case 'Scheduled':
        return 'status-scheduled';
      case 'Conducted':
        return 'status-conducted';
      case 'Cancelled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  };

  const calculateTimeGap = (prevSlot: ActivitySlot, currentSlot: ActivitySlot): number => {
    if (!prevSlot.endTime || !currentSlot.startTime) return 0;
    if (prevSlot.date !== currentSlot.date) return 0;

    const [prevHour, prevMin] = prevSlot.endTime.split(':').map(Number);
    const [currHour, currMin] = currentSlot.startTime.split(':').map(Number);

    const prevMinutes = prevHour * 60 + prevMin;
    const currMinutes = currHour * 60 + currMin;

    return currMinutes - prevMinutes;
  };

  const hasConflict = (slotId: number): boolean => {
    return scheduleData?.conflicts.some(
      (c) => c.slot1 === slotId || c.slot2 === slotId
    ) || false;
  };

  const getConflictMessage = (slotId: number): string | null => {
    const conflict = scheduleData?.conflicts.find(
      (c) => c.slot1 === slotId || c.slot2 === slotId
    );
    return conflict?.message || null;
  };

  const getExportData = () => {
    if (!scheduleData) return { headers: [], rows: [] };

    const headers = [
      'Date',
      'Start Time',
      'End Time',
      'Topic',
      'Timetable',
      'Batches',
      'Room',
      'Shift',
      'Centre',
      'Status',
    ];

    const rows = scheduleData.slots.map((slot) => [
      slot.date || '',
      slot.startTime || '',
      slot.endTime || '',
      slot.topicName || '',
      slot.timetableName || '',
      slot.batches?.map((b) => b.batchName).join('; ') || '',
      slot.roomName || '',
      slot.shiftName || '',
      slot.centreName || '',
      slot.status || '',
    ]);

    return { headers, rows };
  };

  const exportToCSV = () => {
    if (!scheduleData) return;

    const { headers, rows } = getExportData();
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `instructor-schedule-${scheduleData.instructorName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const exportToExcel = () => {
    if (!scheduleData) return;

    const { headers, rows } = getExportData();

    // Create workbook with multiple sheets
    const wb = XLSX.utils.book_new();

    // Sheet 1: Schedule Details
    const scheduleSheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set column widths
    scheduleSheet['!cols'] = [
      { wch: 12 }, // Date
      { wch: 10 }, // Start Time
      { wch: 10 }, // End Time
      { wch: 30 }, // Topic
      { wch: 20 }, // Timetable
      { wch: 25 }, // Batches
      { wch: 15 }, // Room
      { wch: 15 }, // Shift
      { wch: 15 }, // Centre
      { wch: 12 }, // Status
    ];

    XLSX.utils.book_append_sheet(wb, scheduleSheet, 'Schedule');

    // Sheet 2: Analytics Summary
    const analytics = scheduleData.analytics;
    const analyticsData = [
      ['Instructor Analytics'],
      [''],
      ['Instructor Name', scheduleData.instructorName],
      ['Total Sessions', analytics.totalSlots],
      ['Total Teaching Hours', analytics.totalHours],
      ['Number of Timetables', analytics.totalTimetables],
      ['Number of Batches', analytics.totalBatches],
      [''],
      ['Status Breakdown'],
      ['Scheduled Sessions', analytics.statusCounts.scheduled],
      ['Conducted Sessions', analytics.statusCounts.conducted],
      ['Cancelled Sessions', analytics.statusCounts.cancelled],
      ['Upcoming Sessions (7 days)', analytics.upcomingCount],
    ];
    const analyticsSheet = XLSX.utils.aoa_to_sheet(analyticsData);
    analyticsSheet['!cols'] = [{ wch: 25 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, analyticsSheet, 'Analytics');

    // Sheet 3: Conflicts (if any)
    if (scheduleData.conflicts.length > 0) {
      const conflictsData = [
        ['Schedule Conflicts'],
        [''],
        ['Type', 'Message'],
        ...scheduleData.conflicts.map(c => [c.type, c.message])
      ];
      const conflictsSheet = XLSX.utils.aoa_to_sheet(conflictsData);
      conflictsSheet['!cols'] = [{ wch: 15 }, { wch: 50 }];
      XLSX.utils.book_append_sheet(wb, conflictsSheet, 'Conflicts');
    }

    // Export
    XLSX.writeFile(wb, `instructor-schedule-${scheduleData.instructorName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowExportMenu(false);
  };

  const exportToPDF = () => {
    if (!scheduleData) return;

    const { headers, rows } = getExportData();
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

    // Add title
    doc.setFontSize(18);
    doc.text(`${scheduleData.instructorName}'s Schedule`, 14, 15);

    // Add analytics summary
    doc.setFontSize(10);
    const analytics = scheduleData.analytics;
    doc.text(`Total Sessions: ${analytics.totalSlots} | Teaching Hours: ${analytics.totalHours}h | Timetables: ${analytics.totalTimetables} | Batches: ${analytics.totalBatches}`, 14, 25);
    doc.text(`Status: ${analytics.statusCounts.scheduled} Scheduled, ${analytics.statusCounts.conducted} Conducted, ${analytics.statusCounts.cancelled} Cancelled`, 14, 30);

    // Add schedule table
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 35,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [102, 126, 234], // Purple color matching theme
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250],
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Date
        1: { cellWidth: 20 }, // Start Time
        2: { cellWidth: 20 }, // End Time
        3: { cellWidth: 50 }, // Topic
        4: { cellWidth: 35 }, // Timetable
        5: { cellWidth: 40 }, // Batches
        6: { cellWidth: 25 }, // Room
        7: { cellWidth: 25 }, // Shift
        8: { cellWidth: 25 }, // Centre
        9: { cellWidth: 20 }, // Status
      },
    });

    // Add conflicts if any
    if (scheduleData.conflicts.length > 0) {
      const finalY = (doc as any).lastAutoTable.finalY || 35;
      doc.setFontSize(12);
      doc.text('Schedule Conflicts Detected:', 14, finalY + 10);

      const conflictData = scheduleData.conflicts.map((c, idx) => [
        (idx + 1).toString(),
        c.type,
        c.message
      ]);

      autoTable(doc, {
        head: [['#', 'Type', 'Message']],
        body: conflictData,
        startY: finalY + 15,
        styles: {
          fontSize: 9,
        },
        headStyles: {
          fillColor: [252, 129, 129], // Red for conflicts
        },
      });
    }

    // Add footer with timestamp
    const pageCount = doc.getNumberOfPages();
    doc.setFontSize(8);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} | Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    // Save
    doc.save(`instructor-schedule-${scheduleData.instructorName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
    setShowExportMenu(false);
  };

  const renderAnalyticsDashboard = () => {
    if (!scheduleData) return null;

    const { analytics } = scheduleData;

    return (
      <div className="analytics-dashboard">
        <div className="analytics-grid">
          <div className="analytics-card analytics-primary">
            <div className="analytics-icon">📚</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalSlots}</div>
              <div className="analytics-label">Total Sessions</div>
            </div>
          </div>

          <div className="analytics-card analytics-secondary">
            <div className="analytics-icon">⏱️</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalHours}h</div>
              <div className="analytics-label">Teaching Hours</div>
            </div>
          </div>

          <div className="analytics-card analytics-accent">
            <div className="analytics-icon">📋</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalTimetables}</div>
              <div className="analytics-label">Timetables</div>
            </div>
          </div>

          <div className="analytics-card analytics-info">
            <div className="analytics-icon">👥</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.totalBatches}</div>
              <div className="analytics-label">Unique Batches</div>
            </div>
          </div>

          <div className="analytics-card analytics-warning">
            <div className="analytics-icon">📅</div>
            <div className="analytics-content">
              <div className="analytics-value">{analytics.upcomingCount}</div>
              <div className="analytics-label">Upcoming (7 days)</div>
            </div>
          </div>

          <div className="analytics-card analytics-status">
            <div className="analytics-icon">📊</div>
            <div className="analytics-content">
              <div className="analytics-status-grid">
                <div className="status-item">
                  <span className="status-badge status-scheduled">{analytics.statusCounts.scheduled}</span>
                  <span className="status-text">Scheduled</span>
                </div>
                <div className="status-item">
                  <span className="status-badge status-conducted">{analytics.statusCounts.conducted}</span>
                  <span className="status-text">Conducted</span>
                </div>
                <div className="status-item">
                  <span className="status-badge status-cancelled">{analytics.statusCounts.cancelled}</span>
                  <span className="status-text">Cancelled</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFilters = () => {
    return (
      <div className={`filters-panel ${showFilters ? 'filters-visible' : ''}`}>
        <div className="filters-grid">
          <div className="filter-item">
            <label htmlFor="startDate">Start Date</label>
            <input
              type="date"
              id="startDate"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-item">
            <label htmlFor="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              value={filters.endDate || ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-item">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Conducted">Conducted</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="filter-actions">
            <button onClick={clearFilters} className="btn-clear-filters">
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderActivitySlot = (slot: ActivitySlot, index: number, allSlots: ActivitySlot[]) => {
    const prevSlot = index > 0 ? allSlots[index - 1] : null;
    const timeGap = prevSlot ? calculateTimeGap(prevSlot, slot) : 0;
    const hasTimeGap = timeGap > 0;
    const isConflict = hasConflict(slot.id);
    const conflictMsg = getConflictMessage(slot.id);
    const isSelected = selectedSlots.has(slot.id);
    const isUpdating = updatingSlot === slot.id;
    const currentStatus = slot.status as string;

    return (
      <div key={slot.id} className="slot-wrapper">
        {hasTimeGap && (
          <div className="time-gap">
            <span className="time-gap-indicator">
              {timeGap} min break
            </span>
          </div>
        )}
        {isConflict && (
          <div className="conflict-warning">
            <span className="conflict-icon">⚠️</span>
            <span className="conflict-message">{conflictMsg}</span>
          </div>
        )}
        <div className={`activity-slot-card ${isConflict ? 'has-conflict' : ''} ${isSelected ? 'slot-selected' : ''} ${isUpdating ? 'slot-updating' : ''}`}>
          <div className="slot-bar"></div>
          <div className="slot-content">
            <div className="slot-header">
              {bulkMode && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSlotSelection(slot.id)}
                  className="slot-checkbox"
                />
              )}
              <div className="slot-time">
                <span className="time-icon">🕐</span>
                <span className="time-range">
                  {formatTime(slot.startTime)} – {formatTime(slot.endTime)}
                </span>
              </div>
              <span className={`slot-status-badge ${getStatusColor(currentStatus)}`}>
                {slot.status}
              </span>
            {!bulkMode && (
              <div className="slot-actions">
                <button
                  className="action-menu-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenActionMenu(openActionMenu === slot.id ? null : slot.id);
                  }}
                  disabled={isUpdating}
                >
                  ⋮
                </button>
                {openActionMenu === slot.id && (
                  <div className="action-menu">
                    {currentStatus === 'Scheduled' && (
                      <>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          handleQuickStatusUpdate(slot.id, 'Conducted');
                        }}>
                          ✓ Mark as Conducted
                        </button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          handleQuickStatusUpdate(slot.id, 'Cancelled');
                        }}>
                          ✗ Mark as Cancelled
                        </button>
                      </>
                    )}
                    {currentStatus === 'Conducted' && (
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleQuickStatusUpdate(slot.id, 'Cancelled');
                      }}>
                        ✗ Mark as Cancelled
                      </button>
                    )}
                    {currentStatus === 'Cancelled' && (
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleQuickStatusUpdate(slot.id, 'Scheduled');
                      }}>
                        ↻ Reactivate (Scheduled)
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="slot-body">
            <h3 className="slot-topic">{slot.topicName || 'No Topic'}</h3>

            <div className="slot-details">
              {slot.timetableName && (
                <div className="detail-item">
                  <span className="detail-icon">📋</span>
                  <span className="detail-text">{slot.timetableName}</span>
                </div>
              )}

              {slot.roomName && (
                <div className="detail-item">
                  <span className="detail-icon">🏫</span>
                  <span className="detail-text">{slot.roomName}</span>
                </div>
              )}

              {slot.shiftName && (
                <div className="detail-item">
                  <span className="detail-icon">⏰</span>
                  <span className="detail-text">{slot.shiftName}</span>
                </div>
              )}

              {slot.centreName && (
                <div className="detail-item">
                  <span className="detail-icon">📍</span>
                  <span className="detail-text">{slot.centreName}</span>
                </div>
              )}
            </div>

            {typeof slot.studentCount !== 'undefined' && (
              <div className="slot-student-count" onClick={(e) => {
                e.stopPropagation();
                handleShowStudents(slot);
              }} style={{
                cursor: 'pointer',
                padding: '6px 12px',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '8px',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#1976d2',
                border: '1px solid #bbdefb',
                transition: 'all 0.2s ease',
              }}>
                <span>👥</span>
                <span>{slot.studentCount} student{slot.studentCount !== 1 ? 's' : ''}</span>
              </div>
            )}

            {slot.batches && slot.batches.length > 0 && (
              <div className="slot-batches">
                <span className="batches-label">Cohorts</span>
                <div className="batches-chips">
                  {slot.batches.map((batch) => (
                    <span key={batch.id} className="batch-chip">
                      <span className="cohort-dot"></span>
                      {batch.batchName}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {slot.remarks && (
              <div className="slot-remarks">
                <span className="remarks-icon">💬</span>
                <span className="remarks-text">{slot.remarks}</span>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDateGroupedView = () => {
    if (!scheduleData?.groupedByDate) return null;

    return (
      <div className="date-grouped-view">
        {scheduleData.groupedByDate.map((group) => (
          <div key={group.date} className="date-group">
            <div className="date-header">
              <h2 className="date-title">
                <span className="date-icon">📅</span>
                {formatDate(group.date)}
              </h2>
              <span className="date-count">{group.slots.length} sessions</span>
            </div>
            <div className="slots-container">
              {group.slots.map((slot, index) =>
                renderActivitySlot(slot, index, group.slots)
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTimetableGroupedView = () => {
    if (!scheduleData?.groupedByTimetable) return null;

    return (
      <div className="timetable-grouped-view">
        {scheduleData.groupedByTimetable.map((group) => (
          <div key={group.timetableId} className="timetable-group">
            <div className="timetable-header">
              <h2 className="timetable-title">{group.timetableName}</h2>
              <div className="timetable-meta">
                <span className="timetable-count">{group.slots.length} sessions</span>
                {group.effectiveFrom && group.effectiveTo && (
                  <span className="timetable-dates">
                    {new Date(group.effectiveFrom).toLocaleDateString()} - {new Date(group.effectiveTo).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <div className="slots-container">
              {group.slots.map((slot, index) =>
                renderActivitySlot(slot, index, group.slots)
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderGridView = () => {
    if (!scheduleData?.slots) return null;

    // Group slots by timeslot and date
    const gridData: {
      [timeSlot: string]: {
        startTime: string;
        endTime: string;
        slotsByDate: { [date: string]: ActivitySlot[] };
      };
    } = {};

    scheduleData.slots.forEach((slot) => {
      if (!slot.startTime || !slot.endTime || !slot.date) return;

      const timeSlot = `${slot.startTime}-${slot.endTime}`;
      if (!gridData[timeSlot]) {
        gridData[timeSlot] = {
          startTime: slot.startTime,
          endTime: slot.endTime,
          slotsByDate: {},
        };
      }

      if (!gridData[timeSlot].slotsByDate[slot.date]) {
        gridData[timeSlot].slotsByDate[slot.date] = [];
      }

      gridData[timeSlot].slotsByDate[slot.date].push(slot);
    });

    // Get sorted time slots
    const sortedTimeSlots = Object.keys(gridData).sort((a, b) => {
      const timeA = gridData[a].startTime;
      const timeB = gridData[b].startTime;
      return timeA.localeCompare(timeB);
    });

    // Get sorted dates
    const allDates = Array.from(
      new Set(scheduleData.slots.map((s) => s.date).filter(Boolean))
    ).sort() as string[];

    return (
      <div className="grid-view">
        <div className="grid-container">
          {/* Header Row */}
          <div className="grid-header">
            <div className="grid-cell time-header">Time Slot</div>
            {allDates.map((date) => (
              <div key={date} className="grid-cell date-header">
                {formatDate(date)}
              </div>
            ))}
          </div>

          {/* Grid Body */}
          <div className="grid-body">
            {sortedTimeSlots.map((timeSlot) => {
              const { startTime, endTime, slotsByDate } = gridData[timeSlot];
              return (
                <div key={timeSlot} className="grid-row">
                  <div className="grid-cell time-cell">
                    <span className="time-icon">🕐</span>
                    <span className="time-range">
                      {formatTime(startTime)} - {formatTime(endTime)}
                    </span>
                  </div>
                  {allDates.map((date) => {
                    const slots = slotsByDate[date] || [];
                    return (
                      <div key={date} className="grid-cell slot-cell">
                        {slots.map((slot) => (
                          <div
                            key={slot.id}
                            className={`compact-slot-card ${getStatusColor(
                              slot.slotStatus || 'Scheduled'
                            )}`}
                            onClick={() => handleShowStudents(slot)}
                            title={`${slot.topicName}\n${slot.roomName || ''}\n${
                              slot.batches?.map((b) => b.batchName).join(', ') || ''
                            }`}
                          >
                            <div className="compact-topic">{slot.topicName || 'No Topic'}</div>
                            {slot.roomName && (
                              <div className="compact-detail">
                                <span className="detail-icon">🏫</span>
                                <span className="detail-text">{slot.roomName}</span>
                              </div>
                            )}
                            {slot.batches && slot.batches.length > 0 && (
                              <div className="compact-batches">
                                {slot.batches.slice(0, 2).map((batch) => (
                                  <span key={batch.id} className="compact-batch-tag">
                                    {batch.batchName}
                                  </span>
                                ))}
                                {slot.batches.length > 2 && (
                                  <span className="compact-batch-more">
                                    +{slot.batches.length - 2}
                                  </span>
                                )}
                              </div>
                            )}
                            <span className={`compact-status-badge ${getStatusColor(slot.slotStatus || 'Scheduled')}`}>
                              {slot.slotStatus || 'Scheduled'}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderUpcomingSlots = () => {
    if (!scheduleData?.upcomingSlots || scheduleData.upcomingSlots.length === 0) {
      return null;
    }

    return (
      <div className="upcoming-section">
        <h3 className="upcoming-title">Next 5 Upcoming Sessions</h3>
        <div className="upcoming-cards">
          {scheduleData.upcomingSlots.map((slot) => (
            <div key={slot.id} className="upcoming-card">
              <div className="upcoming-date">{formatDate(slot.date)}</div>
              <div className="upcoming-time">{formatTime(slot.startTime)}</div>
              <div className="upcoming-topic">{slot.topicName}</div>
              <div className="upcoming-room">{slot.roomName}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleInstructorSelect = (instructor: Instructor) => {
    setSelectedInstructor(instructor);
    setSearchQuery('');
    setShowSearchResults(false);
    setSearchResults([]);
  };

  const handleClearInstructor = () => {
    setSelectedInstructor(null);
    setSearchQuery('');
  };

  const formatInstructorLabel = (instructor: Instructor): string => {
    const parts = [];
    if (instructor.name) parts.push(instructor.name);
    if (instructor.code) parts.push(`(${instructor.code})`);
    if (instructor.phoneNumber) parts.push(instructor.phoneNumber);
    return parts.join(' ');
  };

  return (
    <div className="teacher-schedule-container">
      {/* Instructor Selector */}
      <div className="instructor-selector-section">
        <div className="instructor-selector-wrapper">
          <div className="selector-label">
            <span className="selector-icon">👤</span>
            <span>View Schedule For:</span>
          </div>
          <div className="selector-input-wrapper">
            {selectedInstructor ? (
              <div className="selected-instructor-display">
                <span className="selected-instructor-name">
                  {selectedInstructor.name || 'Unknown Instructor'}
                </span>
                {selectedInstructor.code && (
                  <span className="selected-instructor-code">({selectedInstructor.code})</span>
                )}
                <button onClick={handleClearInstructor} className="btn-clear-instructor">
                  ✕
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Search by name, code, mobile, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                  className="instructor-search-input"
                />
                {searchLoading && (
                  <div className="search-loading-icon">⏳</div>
                )}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="search-results-dropdown">
                    {searchResults.map((instructor) => (
                      <div
                        key={instructor.id}
                        onClick={() => handleInstructorSelect(instructor)}
                        className="search-result-item"
                      >
                        <div className="result-name">{instructor.name || 'Unknown'}</div>
                        <div className="result-meta">
                          {instructor.code && <span className="result-code">{instructor.code}</span>}
                          {instructor.phoneNumber && <span className="result-phone">{instructor.phoneNumber}</span>}
                          {instructor.email && <span className="result-email">{instructor.email}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {!selectedInstructor && (
            <div className="selector-hint">
              Leave blank to view your own schedule
            </div>
          )}
        </div>
      </div>

      {scheduleData && (
        <div className="teacher-schedule-header">
          <div className="header-content">
            <h1 className="teacher-name">
              {scheduleData.slots && scheduleData.slots[0]?.instructorPhoto ? (
                <img
                  src={scheduleData.slots[0].instructorPhoto}
                  alt={scheduleData.instructorName}
                  className="teacher-header-avatar"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'inline';
                  }}
                />
              ) : null}
              <span className="teacher-icon" style={{ display: (scheduleData.slots && scheduleData.slots[0]?.instructorPhoto) ? 'none' : 'inline' }}>👨‍🏫</span>
              {scheduleData.instructorName}'s Schedule
            </h1>
            <div className="header-actions">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`btn-toggle-filters ${showFilters ? 'active' : ''}`}
              >
                <span className="filter-icon">🔍</span>
                Filters
              </button>
              <button
                onClick={toggleBulkMode}
                className={`btn-bulk-mode ${bulkMode ? 'active' : ''}`}
              >
                <span className="bulk-icon">☑</span>
                {bulkMode ? 'Exit Bulk' : 'Bulk Update'}
              </button>
              <div className="export-dropdown-wrapper">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="btn-export"
                >
                  <span className="export-icon">📥</span>
                  Export
                  <span className="dropdown-arrow">{showExportMenu ? '▲' : '▼'}</span>
                </button>
                {showExportMenu && (
                  <div className="export-dropdown-menu">
                    <button onClick={exportToCSV} className="export-option">
                      <span className="option-icon">📄</span>
                      <div className="option-content">
                        <span className="option-title">CSV</span>
                        <span className="option-desc">Comma-separated values</span>
                      </div>
                    </button>
                    <button onClick={exportToExcel} className="export-option">
                      <span className="option-icon">📊</span>
                      <div className="option-content">
                        <span className="option-title">Excel</span>
                        <span className="option-desc">Multi-sheet workbook</span>
                      </div>
                    </button>
                    <button onClick={exportToPDF} className="export-option">
                      <span className="option-icon">📑</span>
                      <div className="option-content">
                        <span className="option-title">PDF</span>
                        <span className="option-desc">Printable document</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conditional Content Rendering */}
      {loading && (
        <div className="teacher-schedule-loading">
          <div className="loading-spinner"></div>
          <p>Loading schedule...</p>
        </div>
      )}

      {error && !loading && (
        <div className="teacher-schedule-error">
          <div className="error-icon">⚠️</div>
          <p className="error-message">{error}</p>
          <button onClick={loadSchedule} className="btn-retry">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && !effectiveTeacherId && (
        <div className="teacher-schedule-empty">
          <div className="empty-icon">👤</div>
          <h2>Select an Instructor</h2>
          <p>Please search and select an instructor to view their schedule.</p>
        </div>
      )}

      {!loading && !error && effectiveTeacherId && !scheduleData && (
        <div className="teacher-schedule-empty">
          <div className="empty-icon">📅</div>
          <h2>No Schedule Found</h2>
          <p>This instructor doesn't have any scheduled sessions at the moment.</p>
        </div>
      )}

      {!loading && !error && scheduleData && (
        <>
          {renderFilters()}
        </>
      )}

      {!loading && !error && scheduleData && scheduleData.slots.length === 0 && (
        <div className="teacher-schedule-empty">
          <div className="empty-icon">📅</div>
          <h2>No Schedule Found</h2>
          <p>{scheduleData.instructorName} doesn't have any scheduled sessions for the selected date range. Try adjusting the filters above.</p>
        </div>
      )}

      {/* Schedule Content - Only show if we have data */}
      {!loading && !error && scheduleData && scheduleData.slots.length > 0 && (
        <>
          {renderAnalyticsDashboard()}
          {renderUpcomingSlots()}

          <div className="view-mode-selector">
        <button
          onClick={() => setViewMode('date')}
          className={`view-mode-btn ${viewMode === 'date' ? 'active' : ''}`}
        >
          <span className="view-icon">📅</span>
          By Date
        </button>
        <button
          onClick={() => setViewMode('timetable')}
          className={`view-mode-btn ${viewMode === 'timetable' ? 'active' : ''}`}
        >
          <span className="view-icon">📚</span>
          By Timetable
        </button>
        <button
          onClick={() => setViewMode('grid')}
          className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
        >
          <span className="view-icon">⊞</span>
          Grid View
        </button>
      </div>

      <div className="schedule-content">
        {viewMode === 'date' && renderDateGroupedView()}
        {viewMode === 'timetable' && renderTimetableGroupedView()}
        {viewMode === 'grid' && renderGridView()}
      </div>

          {scheduleData.conflicts.length > 0 && (
            <div className="conflicts-summary">
              <h3 className="conflicts-title">
                <span className="conflicts-icon">⚠️</span>
                Schedule Conflicts Detected ({scheduleData.conflicts.length})
              </h3>
              <ul className="conflicts-list">
                {scheduleData.conflicts.map((conflict, index) => (
                  <li key={index} className="conflict-item">
                    <span className={`conflict-type ${conflict.type}`}>
                      {conflict.type === 'room_change' ? '🚶' : '⏰'}
                    </span>
                    <span>{conflict.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Bulk Action Bar */}
      {bulkMode && selectedSlots.size > 0 && (
        <div className="bulk-action-bar">
          <div className="bulk-action-content">
            <span className="bulk-selected-count">
              {selectedSlots.size} slot{selectedSlots.size !== 1 ? 's' : ''} selected
            </span>
            <div className="bulk-action-controls">
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value as 'Scheduled' | 'Conducted' | 'Cancelled')}
                className="bulk-status-select"
              >
                <option value="Conducted">Mark as Conducted</option>
                <option value="Cancelled">Mark as Cancelled</option>
                <option value="Scheduled">Mark as Scheduled</option>
              </select>
              <button
                onClick={handleBulkStatusUpdate}
                disabled={bulkUpdating}
                className="bulk-apply-btn"
              >
                {bulkUpdating ? 'Updating...' : 'Apply to Selected'}
              </button>
              <button onClick={deselectAllSlots} className="bulk-cancel-btn">
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && confirmationData && (
        <div className="confirmation-overlay" onClick={cancelStatusUpdate}>
          <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Status Update</h3>
            <p>
              {confirmationData.isBulk
                ? `Are you sure you want to mark ${confirmationData.slotIds?.length} slot(s) as ${confirmationData.status}?`
                : `Are you sure you want to mark this slot as ${confirmationData.status}?`}
            </p>
            <div className="confirmation-actions">
              <button onClick={confirmStatusUpdate} className="btn-confirm">
                Yes, Update
              </button>
              <button onClick={cancelStatusUpdate} className="btn-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student List Dialog */}
      <StudentListDialog
        open={studentDialogOpen}
        onClose={handleCloseStudentDialog}
        activitySlotId={selectedSlotForStudents?.id || null}
        activitySlotName={selectedSlotForStudents?.name}
      />
    </div>
  );
};

export default TeacherSchedule;
